<div class="draft-email-modal-container">
  <!-- Popup Header -->
  <div class="popup-header">
    <div class="header-left-title">
      <div class="icon-circle">
        <mat-icon>mail_outline</mat-icon>
      </div>
      <div>
        <div class="d-flex align-items-center gap-2">
          <h2>{{ data?.heading || 'View & Edit Draft Email' }}</h2>
          <span class="vessel-badge" *ngIf="data?.vesselName">{{ data.vesselName }}</span>
          <span class="reminder-badge" *ngIf="data?.badge">{{ data.badge }}</span>
          <span class="reminder-badge" *ngIf="data?.reminderLevel && !data?.badge">
            {{ data.reminderLevel === 1 ? '1st Notice' : ('Reminder ' + data.reminderLevel) }}
          </span>
        </div>
        <p class="header-sub">
          Review and customize recipient addresses, subject, and email body content before drafting into mailbox.
        </p>
      </div>
    </div>

    <mat-icon class="close-btn" (click)="onClose()" title="Close popup">close</mat-icon>
  </div>

  <!-- Port call mails: which cargo operation the document list is for -->
  <div class="operation-bar" *ngIf="operation">
    <span class="operation-label">Cargo operation</span>
    <div class="op-toggle" role="radiogroup" aria-label="Cargo operation">
      <button type="button" role="radio" *ngFor="let op of operations" [disabled]="switchingOperation"
        [class.active]="operation === op" [attr.aria-checked]="operation === op" (click)="setOperation(op)">
        {{ op === 'loading' ? 'Loading' : 'Discharging' }}
      </button>
    </div>
    <span class="op-hint" *ngIf="switchingOperation">Updating email…</span>
    <ng-container *ngIf="!switchingOperation">
      <span class="op-hint" *ngIf="data?.operationDetected; else opNotDetected">from the vessel's reports</span>
      <ng-template #opNotDetected><span class="op-warn">Not stated in the reports. Please choose.</span></ng-template>
    </ng-container>
    <span class="op-notice" *ngIf="data?.notice"><mat-icon>history</mat-icon>{{ data.notice }}</span>
  </div>

  <!-- Editable Metadata Fields Form -->
  <div class="fields-section">
    <div class="row g-3">
      <!-- To Field -->
      <div class="col-md-6">
        <div class="field-item">
          <label class="TextLabel">
            <span class="asterik-color">*</span>
            <strong>To:</strong>
            <span class="field-hint">(Primary Recipient)</span>
          </label>
          <div class="input-with-icon">
            <mat-icon class="input-icon to-icon">email</mat-icon>
            <input type="text" class="form-control styled-input" [(ngModel)]="emailTo"
              placeholder="e.g. vessel.shebelle@merakishippingservices.com" />
          </div>
        </div>
      </div>

      <!-- CC Field -->
      <div class="col-md-6">
        <div class="field-item">
          <label class="TextLabel">
            <strong>CC:</strong>
            <span class="field-hint">(Comma-separated)</span>
          </label>
          <div class="input-with-icon">
            <mat-icon class="input-icon cc-icon">people</mat-icon>
            <input type="text" class="form-control styled-input" [(ngModel)]="emailCc"
              placeholder="e.g. operations@merakishippingservices.com" />
          </div>
        </div>
      </div>

      <!-- Subject Field -->
      <div class="col-md-9">
        <div class="field-item">
          <label class="TextLabel">
            <span class="asterik-color">*</span>
            <strong>Subject:</strong>
          </label>
          <div class="input-with-icon">
            <mat-icon class="input-icon subj-icon">subject</mat-icon>
            <input type="text" class="form-control styled-input" [(ngModel)]="emailSubject"
              placeholder="Enter email subject line" />
          </div>
        </div>
      </div>

      <!-- Attachment Chip -->
      <div class="col-md-3 d-flex flex-column justify-content-end">
        <div class="attachment-box" *ngIf="data?.attachmentName">
          <span class="attach-label">Attachment:</span>
          <div class="attach-chip" [title]="data.attachmentName">
            <mat-icon>attach_file</mat-icon>
            <span class="file-name">{{ data.attachmentName }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Email Body Section -->
  <div class="body-editor-section">
    <div class="body-editor-header">
      <label class="TextLabel m-0">
        <strong>Email Body:</strong>
        <span class="field-hint">(Click anywhere in the email below to edit it)</span>
        <span class="edited-badge" *ngIf="bodyEdited">Edited</span>
      </label>

      <!-- Formatting for the selected text; mousedown is cancelled so the selection stays in the email -->
      <div class="body-toolbar" role="toolbar" aria-label="Email body formatting" (mousedown)="$event.preventDefault()">
        <button type="button" class="tool-btn" (click)="format('bold')" title="Bold" aria-label="Bold">
          <mat-icon>format_bold</mat-icon>
        </button>
        <button type="button" class="tool-btn" (click)="format('italic')" title="Italic" aria-label="Italic">
          <mat-icon>format_italic</mat-icon>
        </button>
        <button type="button" class="tool-btn" (click)="format('underline')" title="Underline" aria-label="Underline">
          <mat-icon>format_underlined</mat-icon>
        </button>
        <button type="button" class="tool-btn" (click)="format('insertUnorderedList')" title="Bulleted list"
          aria-label="Bulleted list">
          <mat-icon>format_list_bulleted</mat-icon>
        </button>
        <span class="tool-sep" aria-hidden="true"></span>
        <button type="button" class="tool-btn" (click)="format('undo')" title="Undo" aria-label="Undo">
          <mat-icon>undo</mat-icon>
        </button>
        <button type="button" class="tool-btn" (click)="format('redo')" title="Redo" aria-label="Redo">
          <mat-icon>redo</mat-icon>
        </button>
        <span class="tool-sep" aria-hidden="true"></span>
        <button type="button" class="tool-btn tool-reset" (click)="resetBody()" [disabled]="!bodyEdited"
          title="Discard body edits and restore the generated email">
          <mat-icon>restart_alt</mat-icon> Reset body
        </button>
      </div>
    </div>

    <!-- Optional note from the team; appears in the email (after the greeting) only when filled in -->
    <div class="comment-box">
      <label class="TextLabel" for="draft-comment">
        <strong>Comment:</strong>
        <span class="field-hint">(Optional · added to the email only if you write one)</span>
      </label>
      <textarea id="draft-comment" class="form-control comment-input" rows="2"
        [ngModel]="comment" (ngModelChange)="onCommentChange($event)"
        placeholder="Add a short note for the vessel (up to {{ commentMaxWords }} words)"></textarea>
      <span class="comment-count" [class.at-limit]="commentWords >= commentMaxWords">
        {{ commentWords }}/{{ commentMaxWords }} words
      </span>
    </div>

    <!-- Live Email Viewport -->
    <div class="editor-frame-viewport">
      <iframe #previewIframe class="live-email-iframe" [srcdoc]="safeIframeContent" (load)="onIframeLoad($event)"
        title="Editable Email Preview"></iframe>
    </div>
  </div>

  <!-- Popup Footer Actions -->
  <div class="popup-footer">
    <div class="footer-note">
      <mat-icon>info</mat-icon>
      <span>
        All edits made to To, CC, Subject, and Body will be saved and sent with the draft email.
      </span>
    </div>

    <div class="footer-buttons">
      <button type="button" class="btn btn-light hover-button back-btn" (click)="onClose()">
        <mat-icon>close</mat-icon> Cancel
      </button>

      <button type="button" class="btn btn-primary hover-button apply-btn" (click)="onSend()">
        <mat-icon>send</mat-icon> Draft Email
      </button>
    </div>
  </div>
</div>